/**
 * BulkPasswordDialog.tsx — update many accounts' Maloum passwords in one go.
 *
 * Owner ask (2026-09-22): "we had to change passwords for all our maloum accounts … is there
 * any way to input new logins all at once?" A fleet-wide password change hard-revokes every
 * session, so every account lands on NEEDS_REAUTH and the only path was the per-account
 * Re-auth dialog, 35 times over.
 *
 * Flow: paste `email,password` lines (or pick a CSV) → rows are matched to accounts by email
 * client-side (lib/bulk-password-parse.ts) → Start walks the READY rows ONE AT A TIME through
 * POST /accounts/:id/password (verify with Maloum → store encrypted → reconnect). Sequential
 * on purpose: Maloum's login service rate-limits sign-ins (GoTrue, ~30 per 5 min per IP), so
 * the runner PAUSES on a 429 and retries the same row instead of failing half the list.
 *
 * SECURITY: passwords exist only in this component's state and go straight to our API over
 * the same-origin /api gateway, one account per request. They are never rendered, logged or
 * persisted in the browser; closing the dialog drops the state. The route is Roles(Admin).
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AccountDto } from '@crm/types';
import {
  Badge,
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  useToast,
} from '@crm/ui';
import { accountsApi, ApiRequestError } from '../lib/api-client.js';
import {
  parseBulkPasswordInput,
  type BulkPasswordRow,
  type BulkRowStatus,
} from '../lib/bulk-password-parse.js';

/** Human pacing between accounts — sequential sign-ins, not a burst. */
const ROW_DELAY_MS = 800;
/** How long to wait after Maloum throttles a sign-in before retrying the SAME row. */
const RATE_LIMIT_WAIT_MS = 60_000;
/** Give up on a row after this many throttled attempts (≈5 minutes = one GoTrue window). */
const RATE_LIMIT_MAX_RETRIES = 5;

type RunState = 'running' | 'waiting' | 'ok' | 'failed' | 'stopped';
interface RunResult {
  state: RunState;
  message?: string;
}

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'outline';

const CHECK_LABEL: Record<BulkRowStatus, { text: string; variant: BadgeVariant }> = {
  ready: { text: 'Ready', variant: 'success' },
  unknown_email: { text: 'No account with this email', variant: 'warning' },
  duplicate: { text: 'Duplicate email', variant: 'warning' },
  missing_password: { text: 'Missing password', variant: 'danger' },
  invalid_line: { text: 'Not an email + password line', variant: 'danger' },
};

const RUN_LABEL: Record<RunState, { text: string; variant: BadgeVariant }> = {
  running: { text: 'Updating…', variant: 'info' },
  waiting: { text: 'Paused (throttled)', variant: 'warning' },
  ok: { text: 'Updated', variant: 'success' },
  failed: { text: 'Failed', variant: 'danger' },
  stopped: { text: 'Not run', variant: 'outline' },
};

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

interface BulkPasswordDialogProps {
  accounts: AccountDto[];
  onClose: () => void;
}

export function BulkPasswordDialog({ accounts, onClose }: BulkPasswordDialogProps) {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [text, setText] = React.useState('');
  const [phase, setPhase] = React.useState<'edit' | 'running' | 'done'>('edit');
  const [results, setResults] = React.useState<Record<number, RunResult>>({});
  const [waitLeft, setWaitLeft] = React.useState<number | null>(null);
  const stopRef = React.useRef(false);

  const parsed = React.useMemo(() => parseBulkPasswordInput(text, accounts), [text, accounts]);
  const readyRows = React.useMemo(
    () => parsed.rows.filter((r) => r.status === 'ready' && r.account !== null),
    [parsed],
  );

  const setRow = (lineNo: number, r: RunResult): void =>
    setResults((prev) => ({ ...prev, [lineNo]: r }));

  /** Count down the throttle pause; returns false if Stop was pressed meanwhile. */
  async function waitOutRateLimit(): Promise<boolean> {
    const until = Date.now() + RATE_LIMIT_WAIT_MS;
    while (Date.now() < until) {
      if (stopRef.current) {
        setWaitLeft(null);
        return false;
      }
      setWaitLeft(Math.max(1, Math.ceil((until - Date.now()) / 1000)));
      await sleep(1000);
    }
    setWaitLeft(null);
    return true;
  }

  async function run(rows: BulkPasswordRow[]): Promise<void> {
    stopRef.current = false;
    setPhase('running');
    let ok = 0;
    let failed = 0;
    let stopped = 0;

    for (const row of rows) {
      if (!row.account) continue;
      if (stopRef.current) {
        setRow(row.lineNo, { state: 'stopped', message: 'Stopped before this account' });
        stopped++;
        continue;
      }
      setRow(row.lineNo, { state: 'running' });

      let attempt = 0;
      for (;;) {
        try {
          await accountsApi.setPassword(row.account.id, { password: row.password });
          setRow(row.lineNo, { state: 'ok', message: 'Password verified + stored — reconnecting' });
          ok++;
          break;
        } catch (err) {
          const throttled = err instanceof ApiRequestError && err.status === 429;
          if (throttled && attempt < RATE_LIMIT_MAX_RETRIES) {
            attempt++;
            setRow(row.lineNo, {
              state: 'waiting',
              message: `Maloum is throttling sign-ins — retry ${attempt}/${RATE_LIMIT_MAX_RETRIES} in 60 s`,
            });
            const resume = await waitOutRateLimit();
            if (!resume) {
              setRow(row.lineNo, { state: 'stopped', message: 'Stopped while waiting' });
              stopped++;
              break;
            }
            continue;
          }
          const message =
            err instanceof ApiRequestError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Request failed';
          setRow(row.lineNo, { state: 'failed', message });
          failed++;
          break;
        }
      }

      if (!stopRef.current) await sleep(ROW_DELAY_MS);
    }

    setPhase('done');
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    addToast(
      `Bulk password update: ${ok} updated, ${failed} failed${stopped ? `, ${stopped} not run` : ''}.`,
      failed === 0 && stopped === 0 ? 'success' : 'warning',
    );
  }

  const retryRows = readyRows.filter((r) => {
    const s = results[r.lineNo]?.state;
    return s === 'failed' || s === 'stopped';
  });

  const handleClose = (): void => {
    if (phase === 'running') {
      addToast('Press Stop first — a password update is still running.', 'warning');
      return;
    }
    onClose();
  };

  const handleFile = async (file: File | null): Promise<void> => {
    if (!file) return;
    try {
      setText(await file.text());
      setResults({});
    } catch {
      addToast('Could not read that file.', 'danger');
    }
  };

  const editing = phase === 'edit';

  return (
    <Dialog open title="Bulk password update" onClose={handleClose} className="max-w-3xl">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-muted">
          Paste one account per line as{' '}
          <code className="font-mono text-xs text-text">email,password</code> (a CSV with an{' '}
          <code className="font-mono text-xs text-text">email</code> and{' '}
          <code className="font-mono text-xs text-text">password</code> column works too — extra
          columns are ignored). Each password is verified against Maloum, stored encrypted, and
          the account reconnects. Accounts are processed one at a time; when Maloum throttles
          sign-ins the run pauses for a minute and continues by itself.
        </p>

        <Textarea
          label="Logins — one per line"
          rows={8}
          className="font-mono text-xs"
          placeholder={'creator@example.com,NewPassword123\nother@example.com,AnotherOne!'}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setResults({});
          }}
          disabled={!editing}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-text-muted">
            <span className="font-medium uppercase tracking-wide">or CSV file</span>
            <input
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              disabled={!editing}
              onChange={(e) => {
                void handleFile(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
              className="text-sm text-text file:mr-3 file:px-3 file:py-1 file:text-xs file:rounded file:border file:border-border file:bg-bg-card file:text-text hover:file:bg-bg-hover cursor-pointer"
            />
          </label>
          {parsed.rows.length > 0 && (
            <span className="text-xs text-text-muted ml-auto" aria-live="polite">
              <span className="font-medium text-text">{parsed.ready}</span> ready
              {parsed.problems > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-warning">{parsed.problems}</span> need attention
                </>
              )}
            </span>
          )}
        </div>

        {waitLeft !== null && (
          <p className="text-sm text-warning" role="status">
            Paused — Maloum is throttling sign-ins. Resuming in {waitLeft}s…
          </p>
        )}

        {parsed.rows.length > 0 && (
          <div className="border border-border rounded max-h-[40vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Line</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Check</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parsed.rows.map((row) => {
                  const check = CHECK_LABEL[row.status];
                  const run = results[row.lineNo];
                  return (
                    <TableRow key={row.lineNo}>
                      <TableCell className="text-xs text-text-muted">{row.lineNo}</TableCell>
                      <TableCell className="text-sm">
                        {row.account ? row.account.displayLabel : <span className="text-text-muted">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-text-muted break-all">
                        {row.email || <span className="italic">(none)</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={check.variant} className="text-[10px]">
                          {check.text}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {run ? (
                          <span className="flex flex-col gap-0.5">
                            <Badge variant={RUN_LABEL[run.state].variant} className="text-[10px] w-fit">
                              {RUN_LABEL[run.state].text}
                            </Badge>
                            {run.message && (
                              <span className="text-text-muted">{run.message}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          {phase === 'running' ? (
            <Button
              variant="danger"
              type="button"
              onClick={() => {
                stopRef.current = true;
              }}
            >
              Stop after current
            </Button>
          ) : (
            <Button variant="outline" type="button" onClick={handleClose}>
              {phase === 'done' ? 'Close' : 'Cancel'}
            </Button>
          )}
          {editing && (
            <Button
              type="button"
              disabled={readyRows.length === 0}
              onClick={() => void run(readyRows)}
            >
              Update {readyRows.length} password{readyRows.length === 1 ? '' : 's'}
            </Button>
          )}
          {phase === 'done' && retryRows.length > 0 && (
            <Button type="button" onClick={() => void run(retryRows)}>
              Retry {retryRows.length} failed
            </Button>
          )}
          {phase === 'done' && (
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setResults({});
                setPhase('edit');
              }}
            >
              Start over
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
